import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { sauvegarderSaisie, getSaisieExistante, getTotauxMois, CONCESSIONS } from '../lib/supabase'
import './Dashboard.css'
import './Saisie.css'

export default function Saisie() {
  const { user } = useAuth()
  const now = new Date()
  const today = now.toISOString().split('T')[0]
  const debut60Jours = new Date(now)
  debut60Jours.setDate(debut60Jours.getDate() - 60)
  const debutMoisCourant = debut60Jours.toISOString().split('T')[0]
  const annee = now.getFullYear()
  const mois = now.getMonth() + 1

  const [date, setDate] = useState(today)
  const [concession, setConcession] = useState('')
  const [neuf, setNeuf] = useState(0)
  const [occasion, setOccasion] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [existante, setExistante] = useState(null)
  const [cumuls, setCumuls] = useState(null)

  // Charger saisie existante et cumuls quand date/concession change
  useEffect(() => {
    if (!concession) return
    async function load() {
      const [ex, cum] = await Promise.all([
        getSaisieExistante(date, concession),
        getTotauxMois(annee, mois)
      ])
      setExistante(ex)
      if (ex) { setNeuf(ex.neuf); setOccasion(ex.occasion) }
      else { setNeuf(0); setOccasion(0) }
      setCumuls(cum)
    }
    load()
  }, [date, concession])

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSaved(false)
    try {
      await sauvegarderSaisie({
        date,
        concession,
        neuf: parseInt(neuf) || 0,
        occasion: parseInt(occasion) || 0,
        saisi_par: user.label
      })
      setSaved(true)
      // Refresh cumuls
      const cum = await getTotauxMois(annee, mois)
      setCumuls(cum)
      setTimeout(() => setSaved(false), 4000)
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Réessayez.')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const concInfo = CONCESSIONS.find(c => c.id === concession)
  const cumNeuf = cumuls?.[concession]?.neuf || 0
  const cumOcc = cumuls?.[concession]?.occasion || 0

  return (
    <div className="saisie-page">
      <div className="page-header">
        <div>
          <h1>Saisie journalière</h1>
          <p>Entrez les signatures du jour pour votre concession</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="saisie-controls">
          <div className="form-group">
            <label>Date</label>
           <input
  type="date"
  value={date}
  onChange={e => setDate(e.target.value)}
  min={debutMoisCourant}
  max={today}
/>
          </div>
          {user.isAdmin && (
            <div className="form-group">
              <label>Concession</label>
              <select value={concession} onChange={e => setConcession(e.target.value)} required>
                <option value="">— Choisir —</option>
                {CONCESSIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          )}
        </div>

        {existante && (
          <div className="info-banner">
            Une saisie existe déjà pour cette date : {existante.neuf} neufs · {existante.occasion} occasions. Vous pouvez la modifier ci-dessous.
          </div>
        )}

        {concession && (
          <>
            <div className="saisie-grid">
              <div className="saisie-card">
                <div className="saisie-card-header">
                  <span className="dot dot-blue" />
                  <h3>Véhicules neufs</h3>
                </div>
                <div className="saisie-input-row">
                  <label>Signatures du jour</label>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={neuf}
                    onChange={e => setNeuf(e.target.value)}
                  />
                </div>
                <div className="cumul-info">
                  Cumul du mois : <strong>{cumNeuf + (parseInt(neuf)||0) - (existante?.neuf||0)}</strong>
                </div>
              </div>

              <div className="saisie-card">
                <div className="saisie-card-header">
                  <span className="dot dot-green" />
                  <h3>Véhicules d'occasion</h3>
                </div>
                <div className="saisie-input-row">
                  <label>Signatures du jour</label>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={occasion}
                    onChange={e => setOccasion(e.target.value)}
                  />
                </div>
                <div className="cumul-info">
                  Cumul du mois : <strong>{cumOcc + (parseInt(occasion)||0) - (existante?.occasion||0)}</strong>
                </div>
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}
            {saved && <div className="success-msg">Saisie enregistrée. Le tableau de bord a été mis à jour.</div>}

            <button type="submit" className="submit-btn" disabled={loading || !concession}>
              {loading ? 'Enregistrement…' : existante ? 'Mettre à jour la saisie' : 'Confirmer la saisie'}
            </button>
          </>
        )}
      </form>

      {concession && cumuls && (
        <div className="recap-section">
          <div className="section-title" style={{marginTop: '2rem'}}>Récapitulatif mensuel — toutes concessions</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Concession</th><th>Neufs</th><th>Occasion</th><th>Total</th></tr>
              </thead>
              <tbody>
                {CONCESSIONS.map(c => {
                  const d = cumuls[c.id] || { neuf: 0, occasion: 0 }
                  return (
                    <tr key={c.id} className={c.id === concession ? 'highlight-row' : ''}>
                      <td>
                        <span className="conc-name">{c.label}</span>
                        <span className={`brand-tag tag-${c.marque.toLowerCase()}`}>{c.marque}</span>
                      </td>
                      <td className="num">{d.neuf}</td>
                      <td className="num">{d.occasion}</td>
                      <td className="num bold">{d.neuf + d.occasion}</td>
                    </tr>
                  )
                })}
                <tr className="total-row">
                  <td>Total Groupe</td>
                  <td className="num">{CONCESSIONS.reduce((s,c) => s+(cumuls[c.id]?.neuf||0), 0)}</td>
                  <td className="num">{CONCESSIONS.reduce((s,c) => s+(cumuls[c.id]?.occasion||0), 0)}</td>
                  <td className="num bold">{CONCESSIONS.reduce((s,c) => s+(cumuls[c.id]?.neuf||0)+(cumuls[c.id]?.occasion||0), 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
